import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import { baseurl } from "../../config/url";

const PAGE_SIZE = 20;

// ---------- helpers ----------
const safeText = (v) => (v === null || v === undefined ? "" : String(v));
const safeTextDash = (v) => (v === null || v === undefined || v === "" ? "-" : String(v));
const lower = (v) => safeText(v).toLowerCase();

const inr = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n);
};

const dtIN = (d) => {
  if (!d) return "-";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "-";
  return x.toLocaleString("en-IN");
};

const getToken = () =>
  localStorage.getItem("adminToken") ||
  localStorage.getItem("token") ||
  (() => {
    try {
      return JSON.parse(localStorage.getItem("auth") || "{}")?.token || "";
    } catch {
      return "";
    }
  })();

const pickCategory = (p) => p?.category?.name || p?.categoryName || p?.category || "-";
const pickSellerName = (p) =>
  p?.seller?.companyName || p?.seller?.name || p?.sellerName || p?.seller?.shopName || "-";

const TotalProducts = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // UI state
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest"); // newest | oldest | price-asc | price-desc | stock-asc | stock-desc | value-desc | value-asc
  const [page, setPage] = useState(1);

  // ✅ fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError("");

        const token = getToken();

        const { data } = await axios.get(`${baseurl}/api/product`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          withCredentials: true,
        });

        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.products)
          ? data.products
          : [];

        setProducts(list);
      } catch (err) {
        console.error("Error fetching products:", err.response?.data || err.message);
        setError(err.response?.data?.message || "Failed to load products");
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // ✅ computed totals (all products)
  const totals = useMemo(() => {
    const totalProducts = products.length;

    const totalStock = products.reduce((acc, p) => acc + Number(p?.stock || 0), 0);

    const grandValue = products.reduce((acc, p) => {
      const price = Number(p?.price || 0);
      const stock = Number(p?.stock || 0);
      return acc + price * stock;
    }, 0);

    return { totalProducts, totalStock, grandValue };
  }, [products]);

  // ✅ filter + sort
  const filtered = useMemo(() => {
    let list = [...products];

    const q = lower(search.trim());
    if (q) {
      list = list.filter((p) => {
        return (
          lower(p?._id).includes(q) ||
          lower(p?.name).includes(q) ||
          lower(pickCategory(p)).includes(q) ||
          lower(pickSellerName(p)).includes(q) ||
          lower(p?.saleType).includes(q)
        );
      });
    }

    const byDateDesc = (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0);
    const byDateAsc = (a, b) => new Date(a?.createdAt || 0) - new Date(b?.createdAt || 0);
    const byPriceAsc = (a, b) => Number(a?.price || 0) - Number(b?.price || 0);
    const byPriceDesc = (a, b) => Number(b?.price || 0) - Number(a?.price || 0);
    const byStockAsc = (a, b) => Number(a?.stock || 0) - Number(b?.stock || 0);
    const byStockDesc = (a, b) => Number(b?.stock || 0) - Number(a?.stock || 0);
    const byValueAsc = (a, b) =>
      Number(a?.price || 0) * Number(a?.stock || 0) - Number(b?.price || 0) * Number(b?.stock || 0);
    const byValueDesc = (a, b) =>
      Number(b?.price || 0) * Number(b?.stock || 0) - Number(a?.price || 0) * Number(a?.stock || 0);

    if (sort === "newest") list.sort(byDateDesc);
    if (sort === "oldest") list.sort(byDateAsc);
    if (sort === "price-asc") list.sort(byPriceAsc);
    if (sort === "price-desc") list.sort(byPriceDesc);
    if (sort === "stock-asc") list.sort(byStockAsc);
    if (sort === "stock-desc") list.sort(byStockDesc);
    if (sort === "value-asc") list.sort(byValueAsc);
    if (sort === "value-desc") list.sort(byValueDesc);

    return list;
  }, [products, search, sort]);

  // reset page on search/sort
  useEffect(() => setPage(1), [search, sort]);

  // ✅ pagination
  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(start, start + PAGE_SIZE);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handleDownloadExcel = () => {
    if (!filtered.length) return;

    const excelRows = filtered.map((p, i) => {
      const price = Number(p?.price || 0);
      const stock = Number(p?.stock || 0);

      return {
        "#": i + 1,
        "Product ID": safeTextDash(p?._id),
        "Product Name": safeTextDash(p?.name),
        Category: safeTextDash(pickCategory(p)),
        Seller: safeTextDash(pickSellerName(p)),
        "Sale Type": safeTextDash(p?.saleType),
        "Price (₹)": price,
        Stock: stock,
        "Total Value (₹)": price * stock,
        "Created At": dtIN(p?.createdAt),
      };
    });

    // totals row
    excelRows.push({
      "#": "",
      "Product ID": "",
      "Product Name": "",
      Category: "",
      Seller: "",
      "Sale Type": "",
      "Price (₹)": "",
      Stock: "TOTAL",
      "Total Value (₹)": totals.grandValue,
      "Created At": "",
    });

    const ws = XLSX.utils.json_to_sheet(excelRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inventory");
    XLSX.writeFile(wb, "Product_Inventory.xlsx");
  };

  const openDetails = (id) => {
    // ✅ use your admin route: /products/details/:id
    navigate(`/products/details/${id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="text-lg font-semibold text-gray-800">Loading products…</div>
          <div className="text-sm text-gray-500 mt-1">Please wait</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Total Products</h1>
            <p className="text-sm text-gray-600 mt-1">
              Inventory overview with search, sorting, pagination and Excel export.
            </p>
          </div>

          <button
            onClick={handleDownloadExcel}
            disabled={filtered.length === 0}
            className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
          >
            Download Excel
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
            <div className="text-xs text-gray-500">Total Products</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{totals.totalProducts}</div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
            <div className="text-xs text-gray-500">Total Stock (All)</div>
            <div className="text-2xl font-bold text-indigo-700 mt-1">
              {inr(totals.totalStock)}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
            <div className="text-xs text-gray-500">Grand Inventory Value</div>
            <div className="text-2xl font-bold text-green-700 mt-1">₹{inr(totals.grandValue)}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-500">Search</label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Product / Category / Seller / ID / Sale Type..."
                className="w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            <div>
              <label className="text-xs text-gray-500">Sort</label>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-lg bg-white"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="price-asc">Price Low → High</option>
                <option value="price-desc">Price High → Low</option>
                <option value="stock-asc">Stock Low → High</option>
                <option value="stock-desc">Stock High → Low</option>
                <option value="value-desc">Total Value High → Low</option>
                <option value="value-asc">Total Value Low → High</option>
              </select>
            </div>

            <div className="flex items-end gap-2">
              <button
                onClick={() => setSearch("")}
                className="w-full px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200"
              >
                Clear Search
              </button>
            </div>
          </div>

          <div className="text-sm text-gray-600 mt-3">
            Showing <b>{filtered.length}</b> products
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">#</th>
                <th className="px-4 py-3 text-left font-semibold">Product</th>
                <th className="px-4 py-3 text-left font-semibold">Category</th>
                <th className="px-4 py-3 text-left font-semibold">Seller</th>
                <th className="px-4 py-3 text-left font-semibold">Price</th>
                <th className="px-4 py-3 text-left font-semibold">Stock</th>
                <th className="px-4 py-3 text-left font-semibold">Total Value</th>
                <th className="px-4 py-3 text-right font-semibold">Action</th>
              </tr>
            </thead>

            <tbody>
              {pageItems.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                    No products found.
                  </td>
                </tr>
              ) : (
                pageItems.map((p, idx) => {
                  const rowNo = start + idx + 1;
                  const price = Number(p?.price || 0);
                  const stock = Number(p?.stock || 0);
                  const totalValue = price * stock;

                  return (
                    <tr key={p?._id || rowNo} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-700">{rowNo}</td>

                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{safeTextDash(p?.name)}</div>
                        <div className="text-xs text-gray-500 font-mono break-all">
                          {safeTextDash(p?._id)}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-gray-700">{safeTextDash(pickCategory(p))}</td>
                      <td className="px-4 py-3 text-gray-700">{safeTextDash(pickSellerName(p))}</td>

                      <td className="px-4 py-3 text-gray-700">₹{inr(price)}</td>
                      <td className="px-4 py-3 text-gray-700">{inr(stock)}</td>

                      <td className="px-4 py-3 font-semibold text-gray-900">₹{inr(totalValue)}</td>

                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <button
                            onClick={() => openDetails(p._id)}
                            className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                          >
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>

            {/* Footer total for current filter */}
            {filtered.length > 0 && (
              <tfoot>
                <tr className="bg-green-50 border-t">
                  <td colSpan={6} className="px-4 py-4 text-right font-semibold text-gray-800">
                    Grand Total (Filtered):
                  </td>
                  <td className="px-4 py-4 font-bold text-green-700">₹{inr(
                    filtered.reduce((acc, p) => acc + Number(p?.price || 0) * Number(p?.stock || 0), 0)
                  )}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>

          {/* Pagination */}
          {totalItems > 0 && (
            <div className="flex items-center justify-between gap-3 flex-wrap p-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Page <b>{page}</b> of <b>{totalPages}</b> • Showing{" "}
                <b>{start + 1}</b>–<b>{Math.min(start + PAGE_SIZE, totalItems)}</b> of{" "}
                <b>{totalItems}</b>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Prev
                </button>

                {(() => {
                  const maxBtns = 7;
                  let s = Math.max(1, page - Math.floor(maxBtns / 2));
                  let e = Math.min(totalPages, s + maxBtns - 1);
                  s = Math.max(1, e - maxBtns + 1);

                  const btns = [];
                  for (let p = s; p <= e; p++) btns.push(p);

                  return btns.map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`px-3 py-2 rounded-lg border ${
                        p === page
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-blue-600 border-blue-200 hover:bg-blue-50"
                      }`}
                    >
                      {p}
                    </button>
                  ));
                })()}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* <p className="text-xs text-gray-500 mt-3">
          View opens your admin route: <b>/products/details/:id</b>
        </p> */}
      </div>
    </div>
  );
};

export default TotalProducts;